"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAuthContext = exports.AWSAuthContextProvider = exports.AuthState = void 0;
const Sentry = __importStar(require("@sentry/react"));
const react_1 = __importStar(require("react"));
const global_1 = require("aws-sdk/global");
const client_cognito_identity_1 = require("@aws-sdk/client-cognito-identity");
const amazon_cognito_identity_js_1 = require("amazon-cognito-identity-js");
const promisify_1 = require("./utils/promisify");
const AWSAuthErrors_1 = require("./AWSAuthErrors");
const cognitoHelpers_1 = require("./utils/cognitoHelpers");
const sigv4Fetch_1 = __importDefault(require("./client/sigv4Fetch"));
var AuthState;
(function (AuthState) {
    AuthState["Initializing"] = "initializing";
    AuthState["Error"] = "error";
    AuthState["Unauthenticated"] = "unauthenticated";
    AuthState["Authenticated"] = "authenticated";
})(AuthState = exports.AuthState || (exports.AuthState = {}));
const Context = (0, react_1.createContext)({
    state: AuthState.Initializing,
});
function AWSAuthContextProvider({ config, children, }) {
    const [state, setState] = (0, react_1.useState)(AuthState.Initializing);
    const [user, setUser] = (0, react_1.useState)();
    const [credentials, setCredentials] = (0, react_1.useState)();
    const userPool = (0, react_1.useMemo)(() => {
        return new amazon_cognito_identity_js_1.CognitoUserPool({
            UserPoolId: config.userPoolId,
            ClientId: config.userPoolWebClientId,
        });
    }, []);
    const getCredentials = (0, react_1.useMemo)(() => {
        return function (userSession = null) {
            const Logins = {};
            let IdentityId = undefined;
            if (userSession) {
                Logins[`cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`] = userSession.getIdToken().getJwtToken();
            }
            else {
                IdentityId =
                    window.localStorage.getItem(`aws.cognito.identity-id.${config.identityPoolId}`) || undefined;
            }
            return new global_1.CognitoIdentityCredentials({
                IdentityId,
                IdentityPoolId: config.identityPoolId,
                Logins,
            }, { region: config.region });
        };
    }, [config]);
    /**
     * Should always return the current non-expired Cognito identity credentials.
     */
    const currentCredentials = (0, react_1.useCallback)(() => __awaiter(this, void 0, void 0, function* () {
        try {
            yield credentials.getPromise();
        }
        catch (e) {
            // Refreshes the credentials if needed.
            yield doLogin(userPool.getCurrentUser());
        }
        return credentials;
    }), [credentials]);
    const asyncAuthenticateUser = (0, react_1.useCallback)(function asyncAuthenticateUser(cognitoUser, username, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const authenticationDetails = new amazon_cognito_identity_js_1.AuthenticationDetails({
                Username: username,
                Password: password,
            });
            return new Promise(function (resolve, reject) {
                cognitoUser.authenticateUser(authenticationDetails, {
                    onSuccess: (session) => __awaiter(this, void 0, void 0, function* () {
                        /**
                         * This code was adapted from code meant to work with AWS Amplify. The purpose is so that
                         * if an unauthenticated user has registers, they keep the same IdentityID as they had
                         * before. To be completely honest I'm not really sure how it works, so I left the
                         * original comment below but it references a lot of Amplify stuff.
                         */
                        /*
                         * This call to GetCredentialsForIdentityCommand links the newly authenticated user with the anonymous identity in the Identity Pool.
                         * When https://github.com/aws-amplify/amplify-js/blob/e11e938184fbb935374ca8f19b96365a7d6af7ce/packages/core/src/Credentials.ts#L354
                         * is called further down the chain inside `baseOnSuccess`, it will return the same IdentityId instead of creating a new one.
                         * Thus, the same value of UserId will be used for Pinpoint before and after registration.
                         * In the case when the user is already registered and is signing in (i.e. they already have their account in User Pool linked to an Identity),
                         * this call to GetCredentialsForIdentityCommand will not make any additional links. Instead it will mark the new Identity as disabled and
                         * will merge it into the old one - https://docs.aws.amazon.com/cognito/latest/developerguide/authentication-flow.html (under Merging Identities).
                         * There's also this thread that covers that https://forums.aws.amazon.com/thread.jspa?threadID=238042
                         */
                        if (session) {
                            const cognitoClient = new client_cognito_identity_1.CognitoIdentityClient({
                                region: config.region,
                            });
                            yield cognitoClient.send(new client_cognito_identity_1.GetCredentialsForIdentityCommand({
                                IdentityId: credentials.identityId,
                                Logins: {
                                    [`cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`]: session.getIdToken().getJwtToken(),
                                },
                            }));
                        }
                        resolve(session);
                    }),
                    onFailure: reject,
                    newPasswordRequired: reject,
                });
            });
        });
    }, [config, credentials]);
    /**
     * Used internally to get a user session and an identity ID
     */
    const doLogin = (0, react_1.useCallback)((cognitoUser) => __awaiter(this, void 0, void 0, function* () {
        try {
            if (!cognitoUser) {
                const unauthenticatedCredentials = getCredentials();
                yield unauthenticatedCredentials.getPromise();
                setCredentials(unauthenticatedCredentials);
                setUser(null);
                setState(AuthState.Unauthenticated);
            }
            else {
                const session = yield asyncGetSession(cognitoUser);
                const authenticatedCredentials = getCredentials(session);
                yield authenticatedCredentials.getPromise();
                const userData = yield (0, promisify_1.promisify)(cognitoUser.getUserData, cognitoUser)();
                const attributes = (0, cognitoHelpers_1.userAttributesToObject)(userData.UserAttributes);
                // We need to set the user here
                setCredentials(authenticatedCredentials);
                setUser({
                    email: attributes.email,
                    isTblAccount: attributes.email.endsWith("@thunderbunnylabs.com"),
                });
                setState(AuthState.Authenticated);
            }
        }
        catch (e) {
            Sentry.captureException(e);
            setUser(null);
            setState(AuthState.Error);
        }
    }), []);
    // Auto sign in
    (0, react_1.useEffect)(() => {
        function autoLogin() {
            return __awaiter(this, void 0, void 0, function* () {
                const cognitoUser = userPool.getCurrentUser();
                yield doLogin(cognitoUser);
            });
        }
        autoLogin();
    }, []);
    /**
     * Sign in an unauthenticated user
     */
    function signIn(username, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const cognitoUser = new amazon_cognito_identity_js_1.CognitoUser({
                Username: username,
                Pool: userPool,
            });
            try {
                yield asyncAuthenticateUser(cognitoUser, username, password);
                yield doLogin(cognitoUser);
            }
            catch (e) {
                if (e.name === AWSAuthErrors_1.UserNotConfirmedExceptionName) {
                    throw new AWSAuthErrors_1.UserNotConfirmedException();
                }
                else if (e.name === AWSAuthErrors_1.PasswordResetRequiredExceptionName) {
                    throw new AWSAuthErrors_1.PasswordResetRequiredException();
                }
                else {
                    throw e;
                }
            }
        });
    }
    /**
     * Sign out of the application and clear the cached user
     */
    function signOut() {
        return __awaiter(this, void 0, void 0, function* () {
            const cognitoUser = yield userPool.getCurrentUser();
            if (cognitoUser) {
                // Needed to prevent this issue: https://github.com/aws-amplify/amplify-js/issues/5696
                window.localStorage.removeItem(`aws.cognito.identity-id.${config.identityPoolId}`);
                cognitoUser.signOut();
                yield doLogin(userPool.getCurrentUser());
            }
        });
    }
    /**
     * Returns the new user on success, and throws an error if they were not registered for any reason.
     */
    function signUp(username, password, attributeList = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield (0, promisify_1.promisify)(userPool.signUp, userPool)(username, password, attributeList, null);
            doLogin(result.user);
        });
    }
    /**
     * Verify an email address for a new user
     */
    function verifyEmail(email, password, verificationCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const cognitoUser = new amazon_cognito_identity_js_1.CognitoUser({
                Username: email,
                Pool: userPool,
            });
            // The only non-error result here is a success message.
            yield (0, promisify_1.promisify)(cognitoUser.confirmRegistration, cognitoUser)(verificationCode, true);
            if (password) {
                yield asyncAuthenticateUser(cognitoUser, email, password);
                yield doLogin(cognitoUser);
            }
        });
    }
    /**
     * Re-sends the email verification code to a user.
     */
    function resendEmailVerification(email) {
        return __awaiter(this, void 0, void 0, function* () {
            const cognitoUser = new amazon_cognito_identity_js_1.CognitoUser({
                Username: email,
                Pool: userPool,
            });
            // The only non-error result here is a success message.
            yield (0, promisify_1.promisify)(cognitoUser.resendConfirmationCode, cognitoUser)();
        });
    }
    function sendPasswordReset(email) {
        return __awaiter(this, void 0, void 0, function* () {
            const cognitoUser = new amazon_cognito_identity_js_1.CognitoUser({
                Username: email,
                Pool: userPool,
            });
            // The only non-error result here is a success message.
            yield asyncForgotPassword(cognitoUser);
        });
    }
    function resetPassword(email, verificationCode, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            const cognitoUser = new amazon_cognito_identity_js_1.CognitoUser({
                Username: email,
                Pool: userPool,
            });
            // Throws error for any non-success state
            yield asyncConfirmPassword(cognitoUser, verificationCode, newPassword);
            // Log in automatically
            yield asyncAuthenticateUser(cognitoUser, email, newPassword);
            yield doLogin(cognitoUser);
        });
    }
    function changePassword(oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            const cognitoUser = yield userPool.getCurrentUser();
            yield asyncGetSession(cognitoUser);
            yield asyncChangePassword(cognitoUser, oldPassword, newPassword);
        });
    }
    const authorizedFetch = (0, react_1.useMemo)(() => {
        return (url, options = {}) => __awaiter(this, void 0, void 0, function* () {
            const credentials = yield currentCredentials();
            const fetch = (0, sigv4Fetch_1.default)(credentials);
            return fetch(url, options);
        });
    }, [credentials, currentCredentials]);
    return (react_1.default.createElement(Context.Provider, { value: {
            state,
            user,
            identityId: credentials === null || credentials === void 0 ? void 0 : credentials.identityId,
            isAuthenticated: state === AuthState.Authenticated,
            authorizedFetch,
            currentCredentials,
            signIn,
            signOut,
            signUp,
            resendEmailVerification,
            verifyEmail,
            sendPasswordReset,
            resetPassword,
            changePassword,
        } }, children));
}
exports.AWSAuthContextProvider = AWSAuthContextProvider;
/**
 * Some helper methods to deal with the weird callback structure of Cognito methods.
 */
function asyncGetSession(cognitoUser) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield (0, promisify_1.promisify)(cognitoUser.getSession, cognitoUser)();
    });
}
function asyncForgotPassword(cognitoUser) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise(function (resolve, reject) {
            cognitoUser.forgotPassword({
                onSuccess: resolve,
                onFailure: reject,
            });
        });
    });
}
function asyncConfirmPassword(cognitoUser, verificationCode, newPassword) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise(function (resolve, reject) {
            cognitoUser.confirmPassword(verificationCode, newPassword, {
                onSuccess: resolve,
                onFailure: reject,
            });
        });
    });
}
function asyncChangePassword(cognitoUser, oldPassword, newPassword) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise(function (resolve, reject) {
            cognitoUser.changePassword(oldPassword, newPassword, (error) => {
                if (error) {
                    reject(error);
                }
                else {
                    resolve();
                }
            });
        });
    });
}
function useAuthContext() {
    return (0, react_1.useContext)(Context);
}
exports.useAuthContext = useAuthContext;
//# sourceMappingURL=data:application/json;base64,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